import userService from "@/services/userService"
import api from "@/api"
import healthConnect from "./healthConnectService"
import trainingFeatureAccess from "./trainingFeatureAccess"
import ownHealthFeatureAccess from "./ownHealthFeatureAccess"


async function nativeCall(method, payload = {}) {
  return healthConnect.native(method, payload, method === "healthImportAuthorize" ? 300000 : 30000)
}

function requireSupported(result) {
  if (result?.supported !== true) throw new Error("Bitte die aktuelle App installieren und Gesundheitsdaten im Trainingshub verbinden.")
  return result
}

// Keep native reads and ID-only server lookups bounded, including after a failure.
async function readTogether(items, read) {
  const results = new Array(items.length)
  let next = 0, failed = false
  await Promise.all(Array.from({ length: Math.min(3, items.length) }, async () => {
    while (!failed && next < items.length) {
      const index = next++
      try { results[index] = await read(items[index]) }
      catch (error) { failed = true; throw error }
    }
  }))
  return results
}

function searchGuard(isCurrent = () => true) {
  const access = ownHealthFeatureAccess.capture(), userId = String(userService.getUserId() || "")
  return { access, check() {
    if (!isCurrent() || !ownHealthFeatureAccess.isCurrent(access) || userId !== String(userService.getUserId() || "")) {
      throw new Error("Die Anfrage ist nicht mehr aktuell. Bitte erneut suchen.")
    }
  } }
}

async function attachLinks(result, allowOffline, guard) {
  guard.check()
  const workouts = Array.isArray(result.workouts) ? result.workouts : []
  const ids = [...new Set(workouts.map(item => item.workoutId))]
  const batches = []
  for (let index = 0; index < ids.length; index += 200) batches.push(ids.slice(index, index + 200))
  const responses = await readTogether(batches, async batch => {
    guard.check()
    try {
      // Only record IDs leave the device, never measurements or recording dates.
      const response = await api.workoutApi.getHealthWorkoutLinks(batch)
      guard.check()
      return response
    } catch (error) {
      await ownHealthFeatureAccess.reject(guard.access, error)
      guard.check()
      if (!allowOffline) throw error
      return null
    }
  })
  guard.check()
  const links = new Map(), dismissals = new Map()
  for (const response of responses) {
    for (const link of response?.links || []) links.set(link.workoutId, link)
    for (const link of response?.dismissals || []) {
      if (!dismissals.has(link.workoutId)) dismissals.set(link.workoutId, [])
      dismissals.get(link.workoutId).push(String(link.executionId))
    }
  }
  return { workouts: workouts.map(item => ({ ...item, existingLink: links.get(item.workoutId) || null,
    dismissedExecutionIds: dismissals.get(item.workoutId) || [] })),
    truncated: result.truncated === true, automaticLinking: responses.length > 0 && responses.every(item => item?.automaticLinking === true),
    linkStatusAvailable: responses.every(Boolean),
    readableFrom: typeof result.readableFrom === "string" && Number.isFinite(Date.parse(result.readableFrom)) ? result.readableFrom : null }
}

export default {
  isAndroid: () => healthConnect.isAndroid(),
  async available() {
    const connected = await healthConnect.importAvailable()
    if (connected) return true
    // Preserve the established, explicit import dialog for legacy iPhone admins.
    return healthConnect.isIOS() && trainingFeatureAccess.isAllowed() && (await nativeCall("healthImportCapabilities"))?.supported === true
  },
  async authorize() {
    if (healthConnect.isIOS()) {
      healthConnect.applyStatus(requireSupported(await nativeCall("healthImportAuthorize")))
      return { supported: true }
    }
    return requireSupported(await nativeCall("healthImportCapabilities"))
  },
  async search(range, { allowOffline = false } = {}) {
    const guard = searchGuard()
    const result = requireSupported(await nativeCall("healthImportSearch", range))
    return attachLinks(result, allowOffline, guard)
  },
  async searchRange(range, { allowOffline = false, isCurrent = () => true } = {}) {
    const guard = searchGuard(isCurrent), day = 86400000
    guard.check()
    if (!Number.isFinite(range.from) || !Number.isFinite(range.to) || range.from < Date.UTC(2000, 0, 1) || range.to <= range.from) {
      throw new Error("Bitte einen gültigen Zeitraum auswählen.")
    }
    const capabilities = requireSupported(await nativeCall("healthImportCapabilities"))
    guard.check()
    const largeReads = capabilities.workoutSearchDays === 366
    // Old Android builds enforce a rolling year natively. Do not query decades
    // of empty weekly windows or claim those builds can read the full history.
    const minimum = healthConnect.isAndroid()
      ? healthConnect.state.historyPermissionGranted
        ? largeReads ? Date.UTC(2000, 0, 1) : Date.now() - 365 * day
        : Date.now() - 29 * day
      : Date.UTC(2000, 0, 1)
    const from = Math.max(range.from, minimum), to = Math.min(range.to, Date.now()), windows = []
    const width = (largeReads ? 366 : 7) * day
    for (let start = from; start < to; start += width) windows.push({ from: start, to: Math.min(to, start + width) })
    let failed = false
    const read = async window => {
      guard.check()
      if (failed) throw new Error("Der Abruf wurde beendet.")
      try {
        const result = requireSupported(await nativeCall("healthImportSearch", window))
        guard.check()
        if (failed) throw new Error("Der Abruf wurde beendet.")
        // Dense periods are split instead of silently dropping the oldest records.
        if (result.truncated === true && window.to - window.from > day) {
          const middle = Math.floor((window.from + window.to) / 2)
          const left = await read({ from: window.from, to: middle })
          return left.concat(await read({ from: middle, to: window.to }))
        }
        return [result]
      } catch (error) { failed = true; throw error }
    }
    const results = (await readTogether(windows, read)).flat()
    guard.check()
    const workouts = new Map()
    for (const result of results) for (const item of result.workouts || []) workouts.set(item.workoutId, item)
    const readable = results.map(result => Date.parse(result.readableFrom)).filter(Number.isFinite)
    const oldestSupported = healthConnect.isAndroid() && readable.length !== results.length
      ? Math.max(minimum, Date.now() - 29 * day) : minimum
    return attachLinks({ workouts: [...workouts.values()], truncated: results.some(result => result.truncated === true),
      readableFrom: new Date(readable.length ? Math.max(oldestSupported, Math.min(...readable)) : Math.max(oldestSupported, from)).toISOString() }, allowOffline, guard)
  },
  async read(workoutId) {
    return requireSupported(await nativeCall("healthImportRead", { workoutId })).workout
  },
}
